// src/vendor/store/useVendorStore/index.ts
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

import { callApi } from '@/src/lib/heyApiWrapper'
import { useAuthApiStore } from '../../shared/auth/authApiStore'

// Types
import {
  FullUser,
  VendorIssue,
  Attachment,
  Nodes,
  Watcher,
  WorkflowSection,
  AllocatedToList,
  ElementAnswerList,
  ListProject as Project,
  ScoreGaps,
  IssueStatuses,
  FullSection,
  BaseUser,
} from '@/v31-vendor/types.gen'

// SDK
import * as vendorApi from '@/v31-vendor/sdk.gen'

interface Issue extends VendorIssue {
  scoregapsLoaded?: boolean
  watchlist: Watcher[]
  use_workflow: boolean
}

type ScoreGapsArray = ScoreGaps[]

export const useVendorStore = defineStore('vendor', () => {
  // -----------------------------------------------------------------------------------
  // state
  // -----------------------------------------------------------------------------------
  const issues = ref<Issue[]>([])
  const currentIssue = ref<Issue | null>(null)
  const currentTree = ref<Nodes[] | null>(null)
  const currentSection = ref<FullSection | null>(null)
  const currentProject = ref<Project | null>(null)
  const scoregaps = ref<ScoreGapsArray | null>(null)
  const workflow = ref<WorkflowSection | null>(null)
  const workflowCount = ref<number>(0)
  const attachments = ref<Attachment[] | null>(null)
  const user = ref<FullUser | null>(null)
  const org_users = ref<BaseUser[]>([])

  // -----------------------------------------------------------------------------------
  // getters / derived state
  // -----------------------------------------------------------------------------------
  // prefer simple functions for “curried” getters
  function canManageUsers(permission: string): boolean {
    return user.value !== null && user.value.permissions.includes(permission)
  }

  const canManageAttachments = computed(
    () =>
      currentIssue.value &&
      ['Accepted', 'Updateable'].includes(currentIssue.value?.status)
  )

  const isLoggedIn = computed(() => useAuthApiStore().isAuthenticated)
  const currentUserId = computed(() => useAuthApiStore().currentUserId)
  const currentUserToken = computed(() => useAuthApiStore().accessToken)

  function hasPermission(permission: string): boolean {
    return user.value !== null && user.value.permissions.includes(permission)
  }

  function lookupSectionById(sectionId: number): Nodes | undefined {
    const id = Number(sectionId)
    function recursiveSearch(sections: Nodes[]): Nodes | undefined {
      for (const section of sections) {
        if (section.id === id) return section
        if (section.subsections) {
          const found = recursiveSearch(section.subsections)
          if (found) return found
        }
      }
      return undefined
    }
    return recursiveSearch(currentTree.value || [])
  }

  const isQuestionnaireReadOnly = computed(() => {
    const issue = currentIssue.value
    if (!issue) return false
    if (issue.status === 'Accepted' || issue.status === 'Updateable') {
      if (!issue.deadline) return false
      const deadlineDate = new Date(issue.deadline)
      const now = new Date()
      if (now < deadlineDate) return false
    }
    return true
  })

  const isWinlossCurrentlyExposed = computed(() => {
    const issue = currentIssue.value
    if (!issue || issue.winloss_exposed === false) return false
    const expiryDate = new Date(issue.winloss_expiry || '')
    const now = new Date()
    return now < expiryDate
  })

  // -----------------------------------------------------------------------------------
  // local setters (no side-effects)
  // -----------------------------------------------------------------------------------
  function setIssues(v: Issue[]) {
    issues.value = v
  }
  function setIssue(v: Issue) {
    currentIssue.value = v
  }
  function setIssueTree(v: Nodes[]) {
    currentTree.value = v
  }
  function clearIssueTree() {
    currentTree.value = null
  }
  function clearCurrentIssue() {
    currentIssue.value = null
  }
  function setSection(v: FullSection) {
    currentSection.value = v
  }
  function setWorkflow(v: WorkflowSection) {
    workflow.value = v
  }

  function setAttachments(v: Attachment[]) {
    attachments.value = v
  }
  function setAccepted() {
    if (currentIssue.value) currentIssue.value.status = 'Accepted'
  }
  function setDeclined() {
    if (currentIssue.value) currentIssue.value.status = 'Declined'
  }
  function setSubmitted() {
    if (currentIssue.value) currentIssue.value.status = 'Submitted'
  }
  function setIssueWatchers(watchersDoc: {
    issue_id: number
    watchlist: Watcher[]
  }) {
    if (!currentIssue.value || watchersDoc.issue_id !== currentIssue.value.id)
      return
    currentIssue.value.watchlist = watchersDoc.watchlist
  }
  function setUser(v: FullUser | null) {
    user.value = v
  }
  function setOrgUsers(v: BaseUser[]) {
    org_users.value = v
  }
  function setProject(v: Project) {
    currentProject.value = v
  }

  function logout() {
    setUser(null)
    // authApiStore holds auth state
  }

  // -----------------------------------------------------------------------------------
  // async actions (API)
  // -----------------------------------------------------------------------------------
  async function verify(): Promise<void> {
    const uid = currentUserId.value
    if (!uid) throw new Error('No user ID found')
    const response = await vendorApi.getUser({ query: { targetUser: uid } })
    if (response.data) {
      setUser(response.data)
    }
  }

  async function getIssues() {
    const vendorIssues = await callApi(vendorApi.getIssues)
    const issues = vendorIssues.map((vendorIssue) => {
      return {
        ...vendorIssue,
        scoregapsLoaded: false,
        watchlist: [],
        is_watched: false,
        // Preserve backend-provided workflow flag if present (OpenAPI spec omits it)
        use_workflow:
          (vendorIssue as unknown as { use_workflow?: boolean }).use_workflow ??
          false,
      } as Issue
    })
    setIssues(issues)
  }

  async function getIssue(params: { issueId: number }) {
    const vendorIssue = await callApi(vendorApi.getIssue, {
      path: { issue_id: params.issueId },
    })
    const issue = {
      ...vendorIssue,
      scoregapsLoaded: false,
      watchlist: [],
      is_watched: false,
      use_workflow:
        (vendorIssue as unknown as { use_workflow?: boolean }).use_workflow ??
        false,
    } as Issue
    setIssue(issue)
  }

  async function getIssueTree(params: { issueId: number }) {
    const response = await vendorApi.getIssueTree({
      path: { issue_id: params.issueId },
    })
    if (response.data) {
      const nodes = Array.isArray(response.data)
        ? response.data
        : [response.data]
      setIssueTree(nodes)
    } else {
      setIssueTree([])
    }
  }

  function clear_issue_tree() {
    clearIssueTree()
  }

  async function getSectionAction(params: {
    issueId: number
    sectionId: number
  }) {
    const section = await callApi(vendorApi.getIssueSection, {
      path: {
        issue_id: params.issueId,
        section_id: params.sectionId,
      },
    })
    setSection(section)
  }

  async function getWorkflow(params: {
    issueId: number
    sectionId: number
    page: number
  }) {
    const workflowData = await callApi(vendorApi.getIssueSectionWorkflow, {
      path: {
        issue_id: params.issueId,
        section_id: params.sectionId,
      },
      query: { page: params.page },
    })
    setWorkflow(workflowData)
  }

  async function getScoregaps(issueId: number) {
    return await callApi(vendorApi.getIssueScoregaps, {
      path: {
        issue_id: issueId,
      },
    })
  }

  async function getAttachments(params: { issueId: number }) {
    const attachmentArray = await callApi(vendorApi.getIssueAttachments, {
      path: { issue_id: params.issueId },
    })
    setAttachments(attachmentArray)
  }

  async function setUseWorkflow(params: {
    issueId: number
    useWorkflow: boolean
  }) {
    await vendorApi.postIssueWorkflow({
      path: { issue_id: params.issueId },
      body: { use_workflow: params.useWorkflow },
    })
    if (currentIssue.value) currentIssue.value.use_workflow = params.useWorkflow
  }

  async function getIssueWatchlist(params: { issueId: number }) {
    const response = await vendorApi.getIssueWatchlist({
      path: { issue_id: params.issueId },
    })
    if (!response.data) return
    setIssueWatchers(response.data)
  }

  async function addIssueWatcher(params: { issueId: number; userId: string }) {
    const targetUsersDoc = [{ targetUser: params.userId }]
    await vendorApi.postIssueWatchers({
      path: { issue_id: params.issueId },
      body: targetUsersDoc,
    })
  }

  async function deleteIssueWatcher(params: {
    issueId: number
    userId: string
  }) {
    const targetUsersDoc = [{ targetUser: params.userId }]
    await vendorApi.deleteIssueWatchers({
      path: { issue_id: params.issueId },
      body: targetUsersDoc,
    })
  }

  async function saveAnswer(params: {
    issueId: number
    questionId: number
    answerDoc: ElementAnswerList
  }) {
    await vendorApi.postIssueQuestionAnswers({
      path: {
        issue_id: params.issueId,
        question_id: params.questionId,
      },
      body: params.answerDoc,
    })
  }

  async function acceptIssue(params: { issueId: number }) {
    await vendorApi.postIssueStatus({
      path: { issue_id: params.issueId },
      body: { new_status: 'Accepted' as IssueStatuses },
    })
    setAccepted()
  }

  async function declineIssue(params: { issueId: number }) {
    await vendorApi.postIssueStatus({
      path: { issue_id: params.issueId },
      body: { new_status: 'Declined' as IssueStatuses },
    })
    setDeclined()
  }

  async function submitIssue(params: { issueId: number }) {
    await vendorApi.postIssueStatus({
      path: { issue_id: params.issueId },
      body: { new_status: 'Submitted' as IssueStatuses },
    })
    setSubmitted()
  }

  async function allocateQuestions(params: {
    issueId: number
    alloDoc: AllocatedToList
  }) {
    await vendorApi.postIssueAllocate({
      path: { issue_id: params.issueId },
      body: params.alloDoc,
    })
  }

  async function getUsers() {
    const users = await callApi(vendorApi.getUsers)
    setOrgUsers(users)
  }

  async function getIssueNotes(params: { issueId: number }) {
    const response = await vendorApi.getIssueNotes({
      path: { issue_id: params.issueId },
    })
    return response.data
  }

  async function deleteAnswerElement(params: {
    issueId: number
    elementId: number
  }) {
    await vendorApi.deleteIssueAnswerElement({
      path: {
        issue_id: params.issueId,
        element_id: params.elementId,
      },
    })
  }

  async function uploadAnswerAttachment(params: {
    issueId: number
    formData: FormData
  }) {
    const file = params.formData.get('attachment_upload') as File | Blob | null
    const elId = params.formData.get('el_id') as string | null
    const response = await vendorApi.postIssueAnswerAttachment({
      path: { issue_id: params.issueId },
      body: {
        attachment_upload: file || undefined,
        el_id: elId || undefined,
      },
    })
    return response.data
  }

  // -----------------------------------------------------------------------------------
  // expose
  // -----------------------------------------------------------------------------------
  return {
    // state
    issues,
    currentIssue,
    currentTree,
    currentSection,
    currentProject,
    scoregaps,
    workflow,
    workflowCount,
    attachments,
    user,
    org_users,

    // derived
    canManageUsers,
    canManageAttachments,
    isLoggedIn,
    currentUserId,
    currentUserToken,
    hasPermission,
    lookupSectionById,
    isQuestionnaireReadOnly,
    isWinlossCurrentlyExposed,

    // setters
    setIssues,
    setIssue,
    setIssueTree,
    clearIssueTree,
    clearCurrentIssue,
    setSection,
    setWorkflow,
    setAttachments,
    setUseWorkflow,
    setAccepted,
    setDeclined,
    setSubmitted,
    setIssueWatchers,
    setUser,
    setOrgUsers,
    setProject,
    logout,
    clear_issue_tree,

    // actions
    verify,
    getIssues,
    getIssue,
    getIssueTree,
    getSectionAction, // renamed to avoid colliding with state setter
    getWorkflow,
    getScoregaps,
    getAttachments,
    getIssueWatchlist,
    addIssueWatcher,
    deleteIssueWatcher,
    saveAnswer,
    acceptIssue,
    declineIssue,
    submitIssue,
    allocateQuestions,
    getUsers,
    getIssueNotes,
    deleteAnswerElement,
    uploadAnswerAttachment,
  }
})
